// ═══════════════════════════════════════════════════════════════════════════════
// Guard pin message creation navigation when slots are full (§22)
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useRef } from 'react';
import type { NavigateFunction } from 'react-router-dom';
import type { TotalPinMessageUsageSnapshot } from '@fdh/shared';
import { settingsApi } from '../lib/api';
import { emitPinMessageLimitIfReached } from '../lib/limitError';

export const usePinMessageCreationGate = () => {
  const pinMessagesRef = useRef<TotalPinMessageUsageSnapshot | undefined>(undefined);

  const refreshPinMessagesSnapshot = useCallback(async (): Promise<TotalPinMessageUsageSnapshot | undefined> => {
    const res = await settingsApi.getUsage();
    const snapshot = res.success && res.data ? res.data.pinMessages : undefined;
    pinMessagesRef.current = snapshot;
    return snapshot;
  }, []);

  const guardNavigateToCreatePinMessage = useCallback(
    async (
      navigate: NavigateFunction,
      path: string,
      onBeforeNavigate?: () => void,
    ): Promise<boolean> => {
      const snapshot = pinMessagesRef.current ?? (await refreshPinMessagesSnapshot());
      if (emitPinMessageLimitIfReached(snapshot)) {
        onBeforeNavigate?.();
        return false;
      }
      onBeforeNavigate?.();
      navigate(path);
      return true;
    },
    [refreshPinMessagesSnapshot],
  );

  const guardCreatePinMessageOnMount = useCallback(async (): Promise<boolean> => {
    const snapshot = await refreshPinMessagesSnapshot();
    return !emitPinMessageLimitIfReached(snapshot);
  }, [refreshPinMessagesSnapshot]);

  return {
    guardNavigateToCreatePinMessage,
    guardCreatePinMessageOnMount,
  };
};
